import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import type { PreferredRateOnboardingOption } from 'lumen/components/hotel/wizards/preferred-rate-onboarding/preferred-rate-onboarding-options';
import type HotelWizardsPreferredRateOnboardingRoute from 'lumen/routes/hotel/wizards/preferred-rate-onboarding';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  MEALTYPE_ICONS,
  MEALTYPE_TRANSLATION_KEYS,
  type MealTypeValues,
} from 'lumen/utils/constants/general-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelWizardsPreferredRateOnboardingMealTypeController extends Controller {
  declare model: ModelFrom<HotelWizardsPreferredRateOnboardingRoute>;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service('wizards/preferred-rate-onboarding')
  declare wizardsPreferredRateOnboarding: Services['wizards/preferred-rate-onboarding'];

  @cached
  get mealTypeOptions(): PreferredRateOnboardingOption[] {
    const mealTypes: MealTypeValues[] =
      this.model.requestPricingAssistantRatePreferencesOptionsTaskResult.value
        ?.mealtype ?? [];

    const mappedMealTypes: PreferredRateOnboardingOption[] = mealTypes.map(
      (mealType) => {
        const id = mealType.toString();

        const translationKey = MEALTYPE_TRANSLATION_KEYS[mealType];
        const icon = MEALTYPE_ICONS[mealType];
        const isChecked =
          id === this.wizardsPreferredRateOnboarding.selectedMealTypeId;

        return {
          id,
          label: translationKey ? this.intl.t(translationKey) : id,
          icon,
          isChecked,
        };
      },
    );

    return mappedMealTypes;
  }

  @action onBack(): void {
    this.router.transitionTo('hotel.wizards.preferred-rate-onboarding.persons');
  }

  @action
  async onContinue(): Promise<void> {
    const mergedSettings = {
      ...this.hotelAndMarketService.hotel.paRatePreference,
      ...this.wizardsPreferredRateOnboarding.updatedSettings,
    };

    const result =
      await this.model.saveAndUpdatePreferredRateSettings.perform(
        mergedSettings,
      );

    if (result.isSuccess) {
      this.usageAnalytics.track('Base Rate Settings Changed', {
        eventTrigger: 'base-rate-settings-wizard',
        product: 'pricing-manager',
      });
      this.router.transitionTo('hotel.pricing-manager');
    }
  }

  @action onSkip(): void {
    this.router.transitionTo('hotel.pricing-manager');
  }

  @action
  updateSelectedMealType(value: PreferredRateOnboardingOption): void {
    this.wizardsPreferredRateOnboarding.updateMealType(value.id);
  }
}
